import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, Search, History, BarChart3, Settings, Shield, Menu, X,
  AlertTriangle, Sparkles, Briefcase, Crown, Bell, TrendingUp, TrendingDown,
  Check, Wand2, GraduationCap, Target, RefreshCw, Trophy, Sun, Cpu, LogOut, Flame, BookOpen, Calendar, Zap
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/morning-briefing", icon: Sun, label: "Morning Briefing", badge: "NEW" },
  { path: "/challenge", icon: Trophy, label: "AI Wealth Challenge", badge: "🔥" },
  { path: "/my-portfolios", icon: Briefcase, label: "My HL Portfolios" },
  { path: "/my-fund", icon: Trophy, label: "My Fund", badge: "NEW" },
  { path: "/wizard", icon: Wand2, label: "Portfolio Wizard" },
  { path: "/quick-start", icon: Zap, label: "Quick Start", badge: "NEW" },
  { path: "/quant-tools", icon: BarChart3, label: "Quant Tools" },
  { divider: true, label: "AuraTrade" },
  { path: "/daily-setups", icon: Flame, label: "Daily Setups", badge: "NEW" },
  { path: "/weekly-setups", icon: Calendar, label: "Weekly Setups", badge: "NEW" },
  { path: "/aura-dxy", icon: TrendingUp, label: "DXY Indicator" },
  { path: "/aura-xauusd", icon: Target, label: "XAUUSD (Gold)" },
  { path: "/aura-sp500", icon: BarChart3, label: "S&P 500", badge: "NEW" },
  { path: "/aura-nasdaq", icon: Cpu, label: "NASDAQ 100", badge: "NEW" },
  { path: "/aura-eurusd", icon: Target, label: "EUR/USD" },
  { path: "/aura-gbpusd", icon: Target, label: "GBP/USD" },
  { path: "/aura-gbpjpy", icon: Target, label: "GBP/JPY" },
  { path: "/aura-usdjpy", icon: Target, label: "USD/JPY" },
  { divider: true, label: "Tools" },
  { path: "/trade-journal", icon: BookOpen, label: "Trade Journal", badge: "NEW" },
  { path: "/research", icon: Search, label: "Deep Research" },
  { path: "/optimize", icon: Sparkles, label: "Optimize" },
  { path: "/autonomous", icon: Crown, label: "War Room" },
  { path: "/trades", icon: History, label: "Trade History" },
  { path: "/benchmarks", icon: BarChart3, label: "Benchmarks" },
  { path: "/guide", icon: GraduationCap, label: "User Guide" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

// AlertsContent extracted to avoid nested component definition
const AlertsContent = ({ 
  isMobile = false, 
  totalUnread, 
  markAllRead, 
  eodNotifications, 
  macroAlerts, 
  alerts 
}) => (
  <div className={isMobile ? "w-full" : ""}>
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <h3 className="font-semibold text-sm">Alerts & Notifications</h3>
      {totalUnread > 0 && (
        <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs">
          <Check className="w-3 h-3 mr-1" /> Mark all read
        </Button>
      )}
    </div>

    {/* EOD Sync Notifications */}
    {eodNotifications.length > 0 && (
      <div className="border-b">
        <div className="px-4 py-2 bg-blue-500/10">
          <span className="text-xs font-semibold text-blue-600 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> EOD SYNC UPDATES
          </span>
        </div>
        <div className="max-h-32 overflow-y-auto">
          {eodNotifications.slice(0, 3).map((notif) => (
            <div key={notif.id} className={`px-4 py-2 border-b last:border-0 ${!notif.read ? 'bg-blue-500/5' : ''}`} data-testid={`eod-notif-${notif.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium text-xs leading-tight flex-1">{notif.title}</p>
                {notif.change_pct !== null && notif.change_pct !== undefined && (
                  <span className={`text-xs font-mono ml-2 ${notif.change_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {notif.change_pct >= 0 ? '+' : ''}{notif.change_pct.toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{notif.message}</p>
            </div>
          ))}
        </div>
      </div>
    )}
    
    {/* Macro Alerts */}
    {macroAlerts.length > 0 && (
      <div className="border-b">
        <div className="px-4 py-2 bg-amber-500/10">
          <span className="text-xs font-semibold text-amber-600">⚡ MARKET ALERTS</span>
        </div>
        <div className="max-h-32 overflow-y-auto">
          {macroAlerts.slice(0, 3).map((alert) => (
            <div key={alert.id} className={`px-4 py-2 border-b last:border-0 ${!alert.read ? 'bg-amber-500/5' : ''}`}>
              <p className="font-medium text-xs leading-tight">{alert.title}</p>
              <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{alert.message}</p>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Price Alerts */}
    <div>
      <div className="px-4 py-2 bg-muted/30">
        <span className="text-xs font-semibold text-muted-foreground">📈 PRICE ALERTS</span>
      </div>
      <div className="max-h-32 overflow-y-auto">
        {alerts.length === 0 ? (
          <div className="p-4 text-center text-xs text-muted-foreground">No price alerts</div>
        ) : (
          alerts.slice(0, 3).map((alert) => (
            <div key={alert.id} className={`px-4 py-2 border-b last:border-0 ${!alert.is_read ? 'bg-primary/5' : ''}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-xs">{alert.symbol}</span>
                <span className={`text-xs ${alert.alert_type === 'SURGE_UP' ? 'text-green-500' : 'text-red-500'}`}>
                  {alert.alert_type === 'SURGE_UP' ? '+' : ''}{alert.change_percent?.toFixed(1)}%
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
    
    <div className="px-4 py-2 border-t bg-muted/30">
      <p className="text-[10px] text-muted-foreground text-center">
        EOD sync at 5:30 PM UK • Market alerts every 5 mins • Price alerts at ±5%
      </p>
    </div>
  </div>
);

export const DashboardLayout = () => {
  const location = useLocation();
  const [isPaperTrading, setIsPaperTrading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [macroAlerts, setMacroAlerts] = useState([]);
  const [eodNotifications, setEodNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [macroUnreadCount, setMacroUnreadCount] = useState(0);
  const [eodUnreadCount, setEodUnreadCount] = useState(0);

  const handleLogout = () => {
    localStorage.removeItem("vw_auth_token");
    window.location.href = "/login";
  };

  useEffect(() => {
    fetchSettings();
    fetchAlerts();
    fetchMacroAlerts();
    fetchEodNotifications();
    const interval = setInterval(() => {
      fetchAlerts();
      fetchMacroAlerts();
      fetchEodNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setIsPaperTrading(response.data.is_paper_trading);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts?limit=10`);
      setAlerts(response.data.alerts || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const fetchMacroAlerts = async () => {
    try {
      const response = await axios.get(`${API}/macro-alerts?limit=10`);
      setMacroAlerts(response.data.alerts || []);
      setMacroUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching macro alerts:", error);
    }
  };

  const fetchEodNotifications = async () => {
    try {
      const response = await axios.get(`${API}/eod-notifications?limit=10`);
      setEodNotifications(response.data.notifications || []);
      setEodUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching EOD notifications:", error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/alerts/mark-all-read`);
      await axios.post(`${API}/macro-alerts/mark-all-read`);
      await axios.post(`${API}/eod-notifications/mark-all-read`);
      setUnreadCount(0);
      setMacroUnreadCount(0);
      setEodUnreadCount(0);
      setAlerts(alerts.map(a => ({ ...a, is_read: true })));
      setMacroAlerts(macroAlerts.map(a => ({ ...a, read: true })));
      setEodNotifications(eodNotifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error("Error marking alerts read:", error);
    }
  };

  const togglePaperTrading = async () => {
    try {
      const newValue = !isPaperTrading;
      await axios.put(`${API}/settings`, { 
        is_paper_trading: newValue,
        risk_profile: "moderate_aggressive",
        starting_balance: 10000,
        target_roi: 20,
        auto_rebalance: false,
        notifications_enabled: true
      });
      setIsPaperTrading(newValue);
    } catch (error) {
      console.error("Error updating settings:", error);
    }
  };

  const totalUnread = unreadCount + macroUnreadCount + eodUnreadCount;

  // Props for AlertsContent component
  const alertsProps = {
    totalUnread,
    markAllRead,
    eodNotifications,
    macroAlerts,
    alerts
  };

  return (
    <div className={`dashboard-layout ${isPaperTrading ? 'paper-trading-active' : ''}`}>
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-background/95 backdrop-blur border-b border-border z-40 md:hidden flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </Button>
        
        <div className="flex items-center gap-2">
          <img src="/images/videra-logo.png" alt="Videra Wealth" className="w-7 h-7" />
          <span className="font-bold text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>VIDERA WEALTH</span>
        </div>
        
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5" />
              {totalUnread > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white text-[9px] rounded-full flex items-center justify-center">
                  {totalUnread > 9 ? '9+' : totalUnread}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 mr-2" align="end">
            <AlertsContent isMobile={true} {...alertsProps} />
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && <div className="mobile-overlay md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="dashboard-sidebar">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/images/videra-logo.png" alt="Videra Wealth" className="w-8 h-8" />
              <div>
                <h1 className="text-lg font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Videra Wealth
                </h1>
                <p className="text-[9px] text-muted-foreground uppercase tracking-widest">AI Investment Advisor</p>
              </div>
            </div>
            
            {/* Desktop Bell */}
            <div className="hidden md:block">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="alerts-bell">
                    <Bell className="w-5 h-5" />
                    {totalUnread > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white text-[10px] rounded-full flex items-center justify-center animate-pulse">
                        {totalUnread > 9 ? '9+' : totalUnread}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <AlertsContent {...alertsProps} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Paper Trading Toggle */}
        <div className={`mx-4 mb-4 p-3 rounded-md ${isPaperTrading ? 'paper-mode-indicator bg-warning/5' : 'bg-success/5 border-l-3 border-success'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Trading Mode</span>
            <Switch checked={isPaperTrading} onCheckedChange={togglePaperTrading} data-testid="paper-trading-toggle" />
          </div>
          <div className="flex items-center gap-2">
            {isPaperTrading ? (
              <>
                <AlertTriangle className="w-4 h-4 text-warning" />
                <span className="text-sm font-medium text-warning">Paper Trading</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                <span className="text-sm font-medium text-success">Live Trading</span>
              </>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item, idx) => {
              if (item.divider) {
                return (
                  <li key={`divider-${idx}`} className="pt-3 pb-1">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold px-3">
                      {item.label}
                    </span>
                  </li>
                );
              }
              const isActive = location.pathname === item.path || 
                (item.path === "/research" && location.pathname.startsWith("/research"));
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all duration-200 ${
                      isActive 
                        ? 'bg-primary/10 text-primary border-l-2 border-primary' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon size={18} strokeWidth={1.5} className={item.path === '/autonomous' ? 'text-amber-500' : ''} />
                    <span className="font-medium">{item.label}</span>
                    {item.badge && (
                      <Badge className="ml-auto text-[9px] px-1.5 py-0 bg-amber-500 text-white">{item.badge}</Badge>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 mb-3 rounded-sm text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span className="font-medium">Sign out</span>
          </button>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>v1.0.0</span>
            <Badge variant="outline" className="text-[10px]">UK Markets</Badge>
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground/70">For informational purposes only. Not financial advice.</p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <Outlet context={{ isPaperTrading }} />
      </main>
    </div>
  );
};

export default DashboardLayout;
